import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  FormGroup
} from 'reactstrap';
import Select from 'react-select';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { debounce } from 'lodash';
import { getChildList } from '../../api/child';
import { getBatchInfo, getBatchList } from '../../api/batch';
import { validatePermissionKey } from '../../helpers/permission';
import { VIEW_BATCH, VIEW_CHILDREN } from '../Utility/PermissionKeys';

const FilterModal = ({
  isOpen,
  handleClose,
  filters,
  setFilters,
  setFilterCount,
  authUser
}) => {
  const [tempFilters, setTempFilters] = useState(filters);
  const [childrenList, setChildrenList] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [childOptions, setChildOptions] = useState([]);
  const [childOptionsLoading, setChildOptionsLoading] = useState(false);
  const [batchOptionsLoading, setBatchOptionsLoading] = useState(false);
  const statusOptions = [
    { label: 'Not Answered', value: 'Not Answered' },
    { label: 'Answered', value: 'Answered' },
    { label: 'Published', value: 'Published' }
  ];

  useEffect(() => {
    if (!filters || !authUser) return;
    const handleFetchDefaultChild = async () => {
      setChildOptionsLoading(true);
      const response = await getChildList(1, 1000, {
        filter_children: filters.children
      });
      if (response) {
        setChildrenList(
          response.data.map((item) => ({
            value: item._id,
            label: item.name
          }))
        );
      }
      setChildOptionsLoading(false);
    };
    const handleFetchDefaultBatch = async () => {
      setBatchOptionsLoading(true);
      const response = await getBatchInfo(filters.batchId);
      if (response) {
        setBatchList([
          {
            value: response._id,
            label: response.name
          }
        ]);
      }
      setBatchOptionsLoading(false);
    };
    if (validatePermissionKey(authUser, VIEW_CHILDREN)) {
      handleFetchDefaultChild();
    }
    if (validatePermissionKey(authUser, VIEW_BATCH)) {
      handleFetchDefaultBatch();
    }
  }, [filters, authUser]);

  const searchChild = useCallback(
    debounce((event) => {
      if (event && event !== '') {
        fetchChildOptions(1, 50, event);
      }
    }, 300),
    []
  );
  const searchBatch = useCallback(
    debounce((event) => {
      if (event && event !== '') {
        fetchBatchOptions(1, 50, event);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (validatePermissionKey(authUser, VIEW_CHILDREN)) {
      fetchChildOptions(1, 10);
    }
    if (validatePermissionKey(authUser, VIEW_BATCH)) {
      fetchBatchOptions(1, 10);
    }
  }, []);

  const fetchChildOptions = async (page, limit, searchParam) => {
    setChildOptionsLoading(true);
    const response = await getChildList(
      page,
      limit,
      searchParam && { filter_name: searchParam }
    );
    if (response) {
      setChildOptions(
        response.data.map((item) => ({
          value: item._id,
          label: item.name
        }))
      );
    }
    setChildOptionsLoading(false);
  };
  const fetchBatchOptions = async (page, limit, searchParam) => {
    setBatchOptionsLoading(true);
    const response = await getBatchList(
      page,
      limit,
      searchParam && { name: searchParam }
    );
    if (response) {
      setBatchOptions(
        response.data.map((item) => ({
          value: item._id,
          label: item.name
        }))
      );
    }
    setBatchOptionsLoading(false);
  };

  useEffect(() => {
    if (!filters) return;
    setTempFilters({
      startTime: filters.startTime ? filters.startTime : null,
      endTime: filters.endTime ? filters.endTime : null,
      status: filters.status
        ? { value: filters.status, label: filters.status }
        : null,
      batchId: filters.batchId
        ? {
            value: filters.batchId,
            label: batchList.find(
              (b) => b.value?.toString() === filters.batchId
            )?.label
          }
        : null,
      children: filters.children
        ? filters.children?.split(',')?.map((c) => ({
            value: c,
            label: childrenList.find(
              (cl) => cl?.value?.toString() === c?.toString()
            )?.label
          }))
        : null
    });
  }, [filters, childrenList, batchList]);

  const handleApplyFilter = () => {
    const finalFilters = {};
    let count = 0;
    if (tempFilters.status) {
      finalFilters.status = tempFilters?.status?.value;
      count++;
    }
    if (tempFilters.batchId) {
      finalFilters.batchId = tempFilters?.batchId?.value;
      count++;
    }
    if (tempFilters?.children && tempFilters?.children.length > 0) {
      finalFilters.children = tempFilters?.children
        ?.map((c) => c?.value)
        .join(',');
      count++;
    }
    if (tempFilters?.startTime && tempFilters?.endTime) {
      finalFilters.startTime = tempFilters.startTime;
      finalFilters.endTime = tempFilters.endTime;
      count++;
    }
    setFilters(finalFilters);
    setFilterCount(count);
    handleClose();
  };

  const handleResetFilter = () => {
    setFilterCount(0);
    setFilters({
      status: '',
      startTime: '',
      endTime: '',
      children: '',
      batchId: ''
    });
    handleClose();
  };

  function dateRangeHandler(setHook) {
    return function (dates) {
      const [start, end] = dates;
      setHook((prev) => ({
        ...prev,
        startTime: start,
        endTime: end
      }));
    };
  }

  const selectStyles = {
    menu: (base) => ({
      ...base,
      zIndex: 100
    })
  };

  useEffect(() => {}, [tempFilters]);
  return (
    <React.Fragment>
      <Modal isOpen={isOpen} backdrop="static" size="md">
        <ModalHeader toggle={handleClose}>Filter Queries</ModalHeader>
        <ModalBody>
          <Row>
            <Col md="12">
              <FormGroup>
                <Label>Status</Label>
                <Select
                  options={statusOptions}
                  onChange={(selected) =>
                    setTempFilters((prev) => ({ ...prev, status: selected }))
                  }
                  value={tempFilters.status}
                  styles={selectStyles}
                  isMulti={false}
                  placeholder="Select status"
                />
              </FormGroup>
            </Col>

            {validatePermissionKey(authUser, VIEW_BATCH) && (
              <Col md="12">
                <FormGroup>
                  <Label>Batch</Label>
                  <Select
                    options={batchOptions}
                    onChange={(selected) =>
                      setTempFilters((prev) => ({ ...prev, batchId: selected }))
                    }
                    onInputChange={searchBatch}
                    value={tempFilters.batchId}
                    styles={selectStyles}
                    isMulti={false}
                    placeholder="Select Batch"
                    disabled={batchOptionsLoading}
                    isLoading={batchOptionsLoading}
                  />
                </FormGroup>
              </Col>
            )}

            {validatePermissionKey(authUser, VIEW_CHILDREN) && (
              <Col md="12">
                <FormGroup>
                  <Label>Children</Label>
                  <Select
                    options={childOptions}
                    onChange={(selected) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        children: selected
                      }))
                    }
                    value={tempFilters.children}
                    styles={selectStyles}
                    isMulti={true}
                    onInputChange={searchChild}
                    placeholder="Select Children"
                    disabled={childOptionsLoading}
                    isLoading={childOptionsLoading}
                  />
                </FormGroup>
              </Col>
            )}

            <Col md="12">
              <FormGroup>
                <Label>Asked Time Range</Label>
                <DatePicker
                  selected={tempFilters.startTime}
                  selectsStart
                  className="form-control"
                  startDate={tempFilters.startTime}
                  endDate={tempFilters.endTime}
                  dateFormat="dd-MM-yyyy"
                  selectsRange={true}
                  onChange={dateRangeHandler(setTempFilters)}
                  isClearable={true}
                  placeholderText="Select asked time range"
                />
              </FormGroup>
            </Col>

            <Col>
              <ModalFooter>
                <Button color="primary" size="sm" onClick={handleApplyFilter}>
                  Apply Filter
                </Button>
                <Button color="success" size="sm" onClick={handleResetFilter}>
                  Reset
                </Button>
              </ModalFooter>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    authUser: state.Login?.user
  };
};

export default connect(mapStateToProps)(FilterModal);
